/* eslint-disable react/no-unstable-nested-components */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import StatisticRow from 'src/components/cardLayouts/StatisticRow/StatisticRow';
import NoDataPlaceholder from 'src/components/layouts/NoDataPlaceholder/NoDataPlaceholder';
import { DataView } from 'src/components/ui/TableOverview/DataView';
import { SearchModels } from 'src/components/ui/TableOverview/utils/SearchFieldRegistry';
import { useDataViewController } from 'src/hooks/useDataViewController';
import { localeDate } from 'src/localization/locale';
import { OverlayNavProvider } from 'src/providers/OverlayNavProvider';
import { Invoice, InvoiceTab, useInvoiceSummaryQuery, useInvoicesQuery } from 'src/services/invoices';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import { downloadWithFeedback } from './downloadWithFeedback';
import { formatCount, formatMoney } from './invoiceFormat';

// Tabs are defined here instead of df.quick.filter.state: a quick filter state is
// one field with one value per tab, while "Offen" and "Bezahlt" each combine two
// statuses and "Gutschriften" filters move_type instead of status. The counts come
// from /invoices/summary for the same reason. The tiles use kpiContent instead of
// kpiModel because they are computed live from the mirror; the df.kpi.* framework
// would need its own cron-filled model for values that are already in the table.
const TAB_DOMAINS: Record<InvoiceTab, string> = {
  all: '[]',
  open: '[("status", "in", ["open", "partial"])]',
  overdue: '[("status", "=", "overdue")]',
  paid: '[("status", "in", ["paid", "in_payment"])]',
  credit_notes: '[("move_type", "=", "out_refund")]',
};

function Invoices() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: summary, isLoading: summaryLoading } = useInvoiceSummaryQuery();

  const columnHelper = createColumnHelper<Invoice>();
  const header = (label: string) => () => <span className="typo-text-12 text-faded">{label}</span>;
  const money = (value: number, currency: string | null | undefined) => formatMoney(value, currency);

  const columns = [
    columnHelper.accessor(row => row.name, {
      id: 'name',
      size: 200,
      enableSorting: true,
      meta: { label: t('invoices.columns.number') },
      header: header(t('invoices.columns.number')),
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14--bold text-full truncate">{info.getValue()}</span>
          <span className="typo-text-12 text-faded truncate">
            {info.row.original.is_credit_note ? t('invoices.types.credit_note') : t('invoices.types.invoice')}
            {info.row.original.invoice_origin ? ` · ${info.row.original.invoice_origin}` : ''}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.invoice_date, {
      id: 'invoice_date',
      size: 140,
      enableSorting: true,
      meta: { label: t('invoices.columns.invoice_date') },
      header: header(t('invoices.columns.invoice_date')),
      cell: info => (
        <span className="typo-text-14 text-full">{info.getValue() ? localeDate(info.getValue() as string) : ''}</span>
      ),
    }),
    columnHelper.accessor(row => row.invoice_date_due, {
      id: 'invoice_date_due',
      size: 140,
      enableSorting: true,
      meta: { label: t('invoices.columns.due_date') },
      header: header(t('invoices.columns.due_date')),
      cell: info => (
        <span className={`typo-text-14 ${info.row.original.status === 'overdue' ? 'status-error-full' : 'text-full'}`}>
          {info.getValue() ? localeDate(info.getValue() as string) : ''}
        </span>
      ),
    }),
    columnHelper.accessor(row => row.amount_untaxed, {
      id: 'amount_untaxed',
      size: 140,
      enableSorting: false,
      meta: { label: t('invoices.columns.net') },
      header: header(t('invoices.columns.net')),
      cell: info => (
        <span className="typo-text-14 text-full">{money(info.getValue(), info.row.original.currency_code)}</span>
      ),
    }),
    columnHelper.accessor(row => row.amount_total_signed, {
      id: 'amount_total_signed',
      size: 140,
      enableSorting: true,
      meta: { label: t('invoices.columns.gross') },
      header: header(t('invoices.columns.gross')),
      cell: info => (
        <span className="typo-text-14--bold text-full">{money(info.getValue(), info.row.original.currency_code)}</span>
      ),
    }),
    columnHelper.accessor(row => row.amount_residual_signed, {
      id: 'amount_residual_signed',
      size: 140,
      enableSorting: true,
      meta: { label: t('invoices.columns.open_amount') },
      header: header(t('invoices.columns.open_amount')),
      cell: info => (
        <span className={`typo-text-14 ${info.getValue() !== 0 ? 'text-full' : 'text-faded'}`}>
          {money(info.getValue(), info.row.original.currency_code)}
        </span>
      ),
    }),
    columnHelper.accessor(row => row.status, {
      id: 'status',
      size: 160,
      enableSorting: true,
      meta: { label: t('invoices.columns.status') },
      header: header(t('invoices.columns.status')),
      cell: info => <InvoiceStatusBadge status={info.getValue()} />,
    }),
  ];

  const tiles = useMemo(() => {
    if (!summary) return [];
    const currency = summary.currency_code;
    return [
      {
        title: t('invoices.tiles.open_balance'),
        kpiNumber: money(summary.open_balance, currency),
        description: t('invoices.tiles.open_count', { count: summary.open_count }),
      },
      {
        title: t('invoices.tiles.overdue'),
        kpiNumber: money(summary.overdue_amount, currency),
        description: t('invoices.tiles.overdue_count', { count: summary.overdue_count }),
        progressColor: summary.overdue_count > 0 ? 'var(--status-error-full)' : undefined,
      },
      {
        title: t('invoices.tiles.invoice_count'),
        kpiNumber: formatCount(summary.invoice_count),
        description: t('invoices.tiles.credit_note_count', { count: summary.credit_note_count }),
      },
      {
        title: t('invoices.tiles.last_invoice'),
        kpiNumber: summary.last_invoice_date ? localeDate(summary.last_invoice_date) : '–',
        description: summary.last_synced_at
          ? t('invoices.tiles.last_sync', { date: localeDate(summary.last_synced_at) })
          : undefined,
      },
    ];
  }, [summary, t]);

  const controller = useDataViewController({ sorting: [{ id: 'invoice_date', desc: true }] });
  const [entityIds, setEntityIds] = useState<(string | number)[]>([]);
  const tabs = (['all', 'open', 'overdue', 'paid', 'credit_notes'] as InvoiceTab[]).map(key => ({
    key,
    label: t(`invoices.tabs.${key}`),
    value: key === 'all' ? null : summary?.tab_counts?.[key] ?? null,
  }));

  return (
    <OverlayNavProvider entityIds={entityIds} basePath="/invoices">
      <DataView<Invoice>
        onDataLoaded={items => setEntityIds(items.map(i => i.id))}
        controller={controller}
        modelKey={SearchModels.Invoices}
        filterModelId="df.merchant.invoice"
        headline={t('navbar.invoices')}
        emptyState={<NoDataPlaceholder headline={t('invoices.empty')} />}
        showFilters={false}
        kpiContent={
          <>
            <StatisticRow items={tiles} loading={summaryLoading} />
            {summary && summary.foreign_currency_count > 0 && (
              <span className="typo-text-12 text-faded">
                {t('invoices.tiles.foreign_currency', { count: summary.foreign_currency_count })}
              </span>
            )}
          </>
        }
        tabs={tabs}
        getDomainForTab={tabKey => TAB_DOMAINS[tabKey as InvoiceTab] ?? '[]'}
        actions={[
          {
            title: t('invoices.actions.view'),
            icon: 'edit',
            isRowClickAction: true,
            onExecute: selected => {
              if (selected.length > 0) navigate(`/invoices/${selected[0].id}`);
            },
            showInContextMenu: true,
          },
          {
            title: t('invoices.actions.download_pdf'),
            icon: 'download',
            onExecute: async selected => {
              if (selected.length > 0) await downloadWithFeedback(selected[0], t);
            },
            showInContextMenu: true,
          },
        ]}
        columns={columns}
        useDataQuery={useInvoicesQuery}
      />
    </OverlayNavProvider>
  );
}

export default Invoices;
